import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { emailTemplateService } from '@/api/services/emailTemplateService';
import type { EmailTemplate, EmailTemplateState } from '@/types/emailTemplate';

const initialState: EmailTemplateState = {
  templates: [],
  loading: false,
  error: null,
  selectedTemplate: null,
};

export const fetchEmailTemplates = createAsyncThunk(
  'emailTemplates/fetchAll',
  async () => {
    const response = await emailTemplateService.getEmailTemplates();
    return response.data;
  }
);

export const updateEmailTemplate = createAsyncThunk(
  'emailTemplates/update',
  async ({ id, data }: { id: string; data: Partial<EmailTemplate> }) => {
    const response = await emailTemplateService.updateEmailTemplate(id, data);
    return response;
  }
);

export const deleteEmailTemplate = createAsyncThunk(
  'emailTemplates/delete',
  async (id: string) => {
    await emailTemplateService.deleteEmailTemplate(id);
    return id;
  }
);

const emailTemplatesSlice = createSlice({
  name: 'emailTemplates',
  initialState,
  reducers: {
    setSelectedTemplate: (state, action: PayloadAction<string | null>) => {
      if (action.payload) {
        const template = state.templates.find((t) => t.emailTemplateId === action.payload);
        if (template) {
          state.selectedTemplate = template;
        } else {
          state.selectedTemplate = null;
        }
      } else {
        state.selectedTemplate = null;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmailTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmailTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload;
      })
      .addCase(fetchEmailTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch templates';
      })
      .addCase(updateEmailTemplate.fulfilled, (state, action) => {
        const index = state.templates.findIndex((t) => t.emailTemplateId === action.payload.data.emailTemplateId);
        if (index !== -1) {
          // Update emailSubject, emailBody, and emailBodyJson
          state.templates[index] = {
            ...state.templates[index],
            emailSubject: action.payload.data.emailSubject,
            emailBody: action.payload.data.emailBody,
            emailBodyJson: action.payload.data.emailBodyJson,
          };
        }
        if (state.selectedTemplate?.emailTemplateId === action.payload.data.emailTemplateId) {
          state.selectedTemplate = {
            ...state.selectedTemplate,
            emailSubject: action.payload.data.emailSubject,
            emailBody: action.payload.data.emailBody,
            emailBodyJson: action.payload.data.emailBodyJson,
          }
        }
      })
      .addCase(deleteEmailTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter((t) => t.id !== action.payload);
        if (state.selectedTemplate?.id === action.payload) {
          state.selectedTemplate = null;
        }
      });
  },
});

export const { setSelectedTemplate, clearError } = emailTemplatesSlice.actions;
export default emailTemplatesSlice.reducer;
